import React, { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { PageLayout } from "../../components/PageLayout/PageLayout";
import { useMediaQuery, useTheme } from "@mui/material";
import DailyBudgetInvoiceItemsTable from "../../components/DailyBudgetInvoiceItemsTable/DailyBudgetInvoiceItemsTable";
import useUpdateAuthToken from "../../hooks/useUpdateAuthToken";
import DailyBudgetResume from "../../components/DailyBudgetResume/DailyBudgetResume";
import DailyBudgetDatePicker from "../../components/DailyBudgetDatePicker/DailyBudgetDatePicker";
import Typography from "@mui/material/Typography";
import InitialBudgetSetup from "../../components/InitialBudgetSetup/InitialBudgetSetup";
import Button from "@mui/material/Button";
import { ArrowLeftIcon, ArrowRightIcon } from "@mui/x-date-pickers";
import { formatDateSpanish, getCurrentDate } from "../../utils/format";
import { navigateToDay } from "../shared/utils";
import { DAILY_BUDGET_TEXTS } from "../shared/constants";
import { RootState } from "../../app/store";
import { useGetDailyBudgetListQuery, useGetDailyResumeQuery } from "../../services/budget/budget";
import {
  PageContentBox,
  FlexCenterBox,
  FlexStartBox,
  SectionBox,
  SpacedSectionBox,
  DatePickerWrapper,
  NavigationRow,
  TransactionsRow,
  DesktopOnlyMessageBox,
} from "../shared/styledComponents";

const DailyBudgetPage: React.FC = () => {
  useUpdateAuthToken();
  const token = useSelector((state: RootState) => state.auth.token);
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const date = searchParams.get("date") || getCurrentDate();

  const { data: resumeData, isFetching: isFetchingResume } = useGetDailyResumeQuery(
    { date },
    { skip: !token },
  );

  const { data: budgetList, isFetching: isFetchingBudget } = useGetDailyBudgetListQuery(undefined, {
    skip: !token,
  });

  const currentBudget = useMemo(() => {
    return budgetList?.find((budget) => budget.date === date);
  }, [budgetList, date]);

  const isFetching = isFetchingResume || isFetchingBudget;

  const setDate = useCallback(
    (newDate: string) => {
      setSearchParams({ date: newDate });
    },
    [setSearchParams],
  );

  const handleDateChange = (newDate: string) => {
    setDate(newDate);
  };

  const handlePreviousDay = () => {
    setDate(navigateToDay(date, -1));
  };

  const handleNextDay = () => {
    setDate(navigateToDay(date, 1));
  };

  if (!isDesktop) {
    return (
      <PageLayout showSearch={false}>
        <PageContentBox>
          <DesktopOnlyMessageBox>
            <Typography variant="h5">{DAILY_BUDGET_TEXTS.desktopOnly}</Typography>
          </DesktopOnlyMessageBox>
        </PageContentBox>
      </PageLayout>
    );
  }

  return (
    <PageLayout fullWidth showSearch={false}>
      <PageContentBox>
        <FlexCenterBox>
          <Typography variant={"h2"}>{DAILY_BUDGET_TEXTS.title}</Typography>
        </FlexCenterBox>
        <hr />
        <NavigationRow>
          <Button variant={"outlined"} onClick={handlePreviousDay}>
            <ArrowLeftIcon />
          </Button>
          <Typography variant="h3">{formatDateSpanish(date)}</Typography>
          <Button variant={"outlined"} onClick={handleNextDay}>
            <ArrowRightIcon />
          </Button>
        </NavigationRow>
        <DatePickerWrapper>
          <DailyBudgetDatePicker value={date} onChange={handleDateChange} />
        </DatePickerWrapper>
        <SectionBox>
          <FlexStartBox>
            <Typography variant="h4">{DAILY_BUDGET_TEXTS.section1Title}</Typography>
          </FlexStartBox>
          <Typography variant="h6">{DAILY_BUDGET_TEXTS.section1Description}</Typography>
          <InitialBudgetSetup currentBudget={currentBudget} date={date} isFetching={isFetching} />
          <Typography variant="h6" fontStyle={"italic"}>
            {DAILY_BUDGET_TEXTS.section1Note}
          </Typography>
        </SectionBox>
        <SpacedSectionBox>
          <FlexStartBox>
            <Typography variant="h4">{DAILY_BUDGET_TEXTS.section2Title}</Typography>
          </FlexStartBox>
          <Typography variant={"h6"}>{DAILY_BUDGET_TEXTS.section2Description}</Typography>
          <DailyBudgetResume
            resume={resumeData?.resume}
            currentBudget={currentBudget}
            isFetching={isFetching}
          />
          <Typography variant="h4">{DAILY_BUDGET_TEXTS.section3Title}</Typography>
          <Typography variant="h6">{DAILY_BUDGET_TEXTS.section3Description}</Typography>

          <TransactionsRow>
            <DailyBudgetInvoiceItemsTable
              payments={resumeData?.payments ?? []}
              isFetching={isFetchingResume}
            />
          </TransactionsRow>
        </SpacedSectionBox>
      </PageContentBox>
    </PageLayout>
  );
};

export default DailyBudgetPage;
